import { Box, TextField, TextFieldProps } from '@mui/material';
import { IconButton, colors } from '@sweep-io/sweep-design';
import { ArrowUp } from '@sweep-io/sweep-design/dist/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import SelectedTextBox from './SelectedTextBox';

const StopIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="0.5"
      y="0.5"
      width="11"
      height="11"
      rx="2"
      fill="white"
      style={{ fill: 'white', fillOpacity: 1 }}
    />
  </svg>
);

type TextFieldWithButtonV2Props = TextFieldProps & {
  onConfirm: (message: string) => Promise<void>;
  inputPlaceholder?: string;
  readonly?: boolean;
  isLoading?: boolean;
  value: string;
  setValue: (value: string) => void;
  selectedText?: string | null;
  onClearSelectedText?: () => void;
  maxInputHeight?: number; // Optional prop to limit input height and add scrolling
  renderStopButton?: boolean;
  onStopClick?: () => void;
};

//This is needed for Markdown to identify it as a line break and not a space.
const handleNewlines = (input: string) => input.replace(/\n/g, '  \n');

export const TextFieldWithButtonV2 = ({
  value,
  setValue,
  onConfirm,
  onFocus,
  onBlur,
  inputPlaceholder,
  readonly = false,
  isLoading = false,
  selectedText,
  onClearSelectedText,
  maxInputHeight,
  renderStopButton = false,
  onStopClick,
  ...rest
}: TextFieldWithButtonV2Props) => {
  const isValid = !!value;

  const inputRef = useRef<HTMLInputElement>(null);

  const [focused, setFocused] = useState(false);

  const handleKeyDown = (event: any) => {
    if (!isLoading && event.key === 'Enter' && !event.shiftKey) {
      // If shift is pressed - go down a line.
      // If shift is not pressed - call onConfirm.
      // (default is to go down a line on both cases)
      event.preventDefault();
      _onConfirm();
    }
  };

  //return the focus to the input after getting server response
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  //return the focus to the input after adding reply
  useEffect(() => {
    if (selectedText && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedText]);

  const _onConfirm = useCallback(async () => {
    if (!isValid) {
      return;
    }
    await onConfirm(handleNewlines(value));
    setValue('');
  }, [isValid, onConfirm, value, setValue]);

  const handleClearSelectedText = useCallback(() => {
    onClearSelectedText?.();
  }, [onClearSelectedText]);

  return (
    <TextField
      {...rest}
      inputRef={inputRef}
      onKeyDown={handleKeyDown}
      onChange={(event) => setValue(event.target.value)}
      onFocus={(event) => {
        setFocused(true);
        onFocus && onFocus(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        onBlur && onBlur(event);
      }}
      value={value}
      multiline={true}
      className={classNames({ focused: focused })}
      sx={{
        width: '100%',
        borderRadius: '12px',
        boxShadow: 'none',
      }}
      InputProps={{
        className: classNames({ focused: focused }),
        placeholder: inputPlaceholder,
        disabled: readonly || isLoading,
        sx: {
          width: 'unset',
          minHeight: '56px', //to reach input height
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          '.MuiOutlinedInput-input': {
            minHeight: '20px', //to override default, so it will be centered
          },
          boxShadow: '0px 0px 18px 0px rgba(0, 0, 0, 0.03)',
          borderRadius: '8px',
          '&.MuiInputBase-adornedEnd': {
            padding: '16px',
            '.MuiInputBase-input': {
              width: 'unset',
              paddingRight: '54px', // leave room for the absolutely positioned button
            },
          },
          ...(maxInputHeight && {
            '& textarea': {
              maxHeight: `${maxInputHeight}px !important`,
              overflowY: 'scroll !important',
              resize: 'none',
            },
          }),
        },
        startAdornment: selectedText && (
          <SelectedTextBox selectedText={selectedText} onCloseClick={handleClearSelectedText} />
        ),
        endAdornment: (
          <Box
            sx={{
              position: 'absolute',
              right: '16px',
              bottom: '12px',
              // UX request to make an exception from the design system
              '.MuiButton-root:disabled': {
                borderColor: colors.blue[300],
                backgroundColor: colors.blue[300],
                'svg path': {
                  stroke: colors.white,
                },
                '.MuiCircularProgress-root': {
                  color: colors.white,
                },
              },
            }}
          >
            {renderStopButton ? (
              <IconButton
                size="small"
                variant="primary"
                onClick={onStopClick}
                disabled={readonly}
                loading={isLoading}
              >
                <StopIcon />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                variant="primary"
                onClick={_onConfirm}
                loading={isLoading}
                disabled={isLoading || readonly || !isValid}
              >
                <ArrowUp />
              </IconButton>
            )}
          </Box>
        ),
      }}
    />
  );
};
